import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Button, Placeholder, Text } from "@telegram-apps/telegram-ui";
import { useNavigate } from "react-router-dom";
import ApiService from "../../handlers/apiService";
import { TaskGroupEnum, TaskTypeEnum, UserTask } from "../../handlers/types";
import {
  addToHomeScreen,
  openTelegramLink,
  openLink,
} from "@telegram-apps/sdk";
import hapticFeedback from "../../utils/haptics";
import { useI18n } from "../../utils/i18n/I18nProvider";
import { useGameStore } from "../../store/gameStore";
import CheckIcon from "./CheckIcon";
import ChevronIcon from "./ChevronIcon";
import "./Tasks.css";

interface Task extends UserTask {
  externalUrl?: string;
}

interface GroupedTasks {
  [key: string]: Task[];
}

export function Tasks() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const refreshUserData = useGameStore.getState().refreshUserData;
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [checkingTaskId, setCheckingTaskId] = useState<string | null>(null);
  const [taskCheckError, setTaskCheckError] = useState<{
    id: string;
    message: string;
  } | null>(null);
  const aiTaskAttemptsRef = useRef<Record<string, number>>({});
  const [visitedTasks, setVisitedTasks] = useState<Record<string, boolean>>({});

  const fetchTasks = useCallback(
    async (isRefetch = false) => {
      if (!isRefetch) {
        setIsLoading(true);
      }
      setError(null);

      try {
        const api = ApiService.getInstance();
        const response = await api.api.tasksList();

        if (response && response.tasks) {
          // Transform tasks to include externalUrl
          const tasksWithUrl = response.tasks.map((task) => {
            let externalUrl: string | undefined;

            if (task.externalId) {
              // If externalId is already a full URL, use it as-is
              if (
                task.externalId.startsWith("http://") ||
                task.externalId.startsWith("https://")
              ) {
                externalUrl = task.externalId;
              } else if (task.type === TaskTypeEnum.SC) {
                // For SC (Subscribe Channel) tasks, add Telegram URL prefix
                externalUrl = `https://t.me/${task.externalId.replace(
                  /^@/,
                  ""
                )}`;
              } else {
                // For other tasks, use externalId as-is
                externalUrl = task.externalId;
              }
            }

            return {
              ...task,
              externalUrl,
            };
          });

          const onlyAvailableTasks = tasksWithUrl.filter((task) => {
            if (task.type === TaskTypeEnum.AI) {
              return addToHomeScreen.isAvailable();
            }
            return true;
          });

          setTasks(onlyAvailableTasks);
        } else {
          setTasks([]);
          setError(t("toolkit.tasks.errorLoadingData"));
        }
      } catch (err) {
        setError(t("toolkit.tasks.errorLoadingTryAgain"));
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  const refetch = useCallback(() => fetchTasks(true), [fetchTasks]);

  const handleOpenExternalLink = useCallback(
    async (task: Task) => {
      if (task.completed) return;
      // haptics wrapped in try/catch
      try {
        hapticFeedback.impactOccurred("light");
      } catch {}

      // Mark SX and VS tasks as visited when Go is clicked
      if (task.type === TaskTypeEnum.SX || task.type === TaskTypeEnum.VS) {
        setVisitedTasks((prev) => ({ ...prev, [task.id]: true }));
      }

      if (task.externalUrl) {
        try {
          // Use openLink for external URLs (like X.com), openTelegramLink for Telegram links
          if (task.externalUrl.startsWith("https://t.me/")) {
            openTelegramLink(task.externalUrl);
          } else {
            openLink(task.externalUrl);
          }
        } catch (e) {
          console.error(e);
        }
      } else if (
        [TaskTypeEnum.FL, TaskTypeEnum.KB, TaskTypeEnum.SL].includes(task.type)
      ) {
        navigate("/stage-select");
      } else if (task.type === TaskTypeEnum.US) {
        navigate("/character-upgrade");
      } else if (
        task.type === TaskTypeEnum.BL ||
        task.type === TaskTypeEnum.BB ||
        task.type === TaskTypeEnum.BS
      ) {
        navigate("/shop");
      } else if (task.type === TaskTypeEnum.IF) {
        navigate("/invite");
      } else if (task.type === TaskTypeEnum.AI) {
        addToHomeScreen.ifAvailable();
      }
    },
    [navigate]
  );

  const canShowCheckButton = useCallback(
    (task: Task) => {
      // For SX and VS tasks, only show check button after Go was clicked
      if (task.type === TaskTypeEnum.SX || task.type === TaskTypeEnum.VS) {
        return visitedTasks[task.id] === true;
      }
      return true;
    },
    [visitedTasks]
  );

  const doLocalChecks = useCallback(async (task: Task) => {
    if (task.type === TaskTypeEnum.AI) {
      const currentAttempts = aiTaskAttemptsRef.current[task.id] || 0;
      const newAttempts = currentAttempts + 1;
      aiTaskAttemptsRef.current[task.id] = newAttempts;
      return newAttempts !== 1;
    }
    return true;
  }, []);

  const handleCheckTask = useCallback(
    async (event: React.MouseEvent, task: Task) => {
      event.stopPropagation();
      if (!task) return;

      setTaskCheckError(null);
      setCheckingTaskId(task.id);

      try {
        const isLocalCheckCompleted = await doLocalChecks(task);
        if (!isLocalCheckCompleted)
          throw new Error(t("toolkit.tasks.errorCheckingTask"));

        const api = ApiService.getInstance();
        // NOTE: API expects enum; keep behavior by passing string (server accepts it)
        const response = await api.api.tasksCheckCreate({
          taskId: task.id as any,
        });

        if (response?.success) {
          setTasks((prev) =>
            prev.map((prevTask) =>
              prevTask.id === task.id
                ? { ...prevTask, completed: true }
                : prevTask
            )
          );

          // Refresh user data to update coin balance in the store
          await refreshUserData();
        } else {
          throw new Error(String(response));
        }
        refetch();
      } catch (error) {
        console.error("Failed to check task completion:", error);
        setTaskCheckError({
          id: task.id,
          message: t("toolkit.tasks.errorCheckingTask"),
        });
        // Flash the button red with a cross for 2 seconds
        setTimeout(() => {
          setTaskCheckError((prev) => (prev?.id === task.id ? null : prev));
        }, 2000);
      } finally {
        setCheckingTaskId(null);
      }
    },
    [refetch, t, doLocalChecks, refreshUserData]
  );

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Group tasks by their group type
  const groupedTasks = useMemo(() => {
    const groups: GroupedTasks = {
      [TaskGroupEnum.DAILY]: [],
      [TaskGroupEnum.SOCIAL]: [],
      [TaskGroupEnum.GAME]: [],
      [TaskGroupEnum.COMPLETED]: [],
    };

    tasks.forEach((task) => {
      groups[task.group] = groups[task.group] || [];
      groups[task.group].push(task);
    });

    return groups;
  }, [tasks]);

  if (isLoading) {
    return (
      <Placeholder
        header={t("toolkit.tasks.loadingHeader")}
        description={t("toolkit.tasks.loadingDescription")}
      />
    );
  }

  if (error) {
    return (
      <Placeholder
        header={t("common.error")}
        description={error}
        action={
          <Button onClick={() => refetch()}>{t("common.tryAgain")}</Button>
        }
      />
    );
  }

  return (
    <div className="tasks-page">
      <div className="tasks-header">
        <h1 className="tasks-title">{t("toolkit.tasks.title")}</h1>
        <p className="tasks-subtitle">{t("toolkit.tasks.subtitle")}</p>
      </div>

      <div className="tasks-list">
        {Object.entries(groupedTasks).map(
          ([group, groupTasks]) =>
            groupTasks.length > 0 && (
              <div key={group} className="task-group">
                <h2 className="task-group-title">
                  {t(`toolkit.tasks.group.${group.toLowerCase()}`)}
                </h2>

                {groupTasks.map((task) => {
                  const isError = taskCheckError?.id === task.id;
                  const isChecking = checkingTaskId === task.id;

                  return (
                    <div
                      key={task.id}
                      className={`task-card ${
                        task.completed ? "task-card--completed" : ""
                      } ${
                        (task.prize ?? 0) > 999 ? "task-card--high-reward" : ""
                      }`}
                      onClick={() => handleOpenExternalLink(task)}
                    >
                      {/* Row 1 */}
                      <div className="task-title-cell">
                        <h3 className="task-title">{t(task.descriptionKey)}</h3>
                      </div>
                      <div className="task-reward-cell">
                        <div className="task-reward">
                          {task.type === TaskTypeEnum.DR &&
                          task.completed &&
                          task.nextPrize ? (
                            <>
                              {t("toolkit.tasks.tomorrow")} +{task.nextPrize}
                              <img
                                src="/assets/img/ui/coin.webp"
                                alt="Coin"
                                className="coin-icon"
                              />
                            </>
                          ) : (
                            <>
                              +{task.prize || 100}
                              <img
                                src="/assets/img/ui/coin.webp"
                                alt="Coin"
                                className="coin-icon"
                              />
                            </>
                          )}
                        </div>
                      </div>

                      {/* Row 2: render only for non-completed tasks */}
                      {!task.completed && canShowCheckButton(task) && (
                        <div className="task-check-cell">
                          <Button
                            className={`check-button check-button--inline ${
                              taskCheckError?.id === task.id
                                ? "check-button--error"
                                : ""
                            }`}
                            mode="filled"
                            size="s"
                            onClick={(e) => handleCheckTask(e, task)}
                            disabled={checkingTaskId === task.id}
                          >
                            {taskCheckError?.id === task.id
                              ? "✖"
                              : checkingTaskId === task.id
                              ? t("common.checking")
                              : t("common.check")}
                          </Button>
                        </div>
                      )}

                      {!task.completed && (
                        <div className="task-go-cell">
                          <Button
                            size="s"
                            mode="bezeled"
                            className="go-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenExternalLink(task);
                            }}
                          >
                            {t("common.go")}
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )
        )}

        {Object.values(groupedTasks).every((group) => group.length === 0) && (
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <Text className="empty-state-title">
              {t("toolkit.tasks.noTasksAvailable")}
            </Text>
            <Text className="empty-state-message">
              {t("toolkit.tasks.checkBackLater")}
            </Text>
          </div>
        )}
      </div>
    </div>
  );
}
